import * as Sentry from "@sentry/browser"
import { ref } from "vue"
import { analytics } from "@/utils/analytics"
import type { CtaSource } from "@/utils/ctaSource"
import { googleAuthorizationUrl } from "@/utils/testAuthentication"

export function useOAuthSignIn(getRedirectPath: () => string | undefined, source: CtaSource) {
	const isSubmitting = ref(false)
	const authError = ref("")

	const signInWithGoogle = async () => {
		isSubmitting.value = true
		authError.value = ""

		try {
			// Instead of using Supabase for that we create the Google OAuth URL ourselves
			// We had to use this approach so we could see Landerz on the google consent screen
			// For some reason that I could not figure out, if we use Supabase's built-in Google OAuth
			// it shows XXXXX.supabase.co on the consent screen which is not very professional
			const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
			const redirectUri = `${window.location.origin}/auth/callback`
			const scope = "openid email profile"
			const state = Math.random().toString(36).substring(7)

			if (!clientId) {
				throw new Error("Google Client ID is not configured")
			}

			// keep the state in localStorage for verification and then remove it after in AuthCallbackPage.vue
			localStorage.setItem("oauth_state", state)
			// Store the provider type so we can detect it in the callback
			localStorage.setItem("oauth_provider", "google")
			localStorage.setItem("oauth_source", source)

			// Store the redirect parameter if exists, so we can use it after OAuth callback
			const redirectPath = getRedirectPath()
			if (redirectPath) {
				localStorage.setItem("oauth_redirect", redirectPath)
			}

			// Build the Google OAuth URL
			const authUrl = new URL(googleAuthorizationUrl)
			authUrl.searchParams.set("client_id", clientId)
			authUrl.searchParams.set("redirect_uri", redirectUri)
			authUrl.searchParams.set("response_type", "code")
			authUrl.searchParams.set("scope", scope)
			authUrl.searchParams.set("state", state)
			authUrl.searchParams.set("access_type", "offline")
			authUrl.searchParams.set("prompt", "consent")

			analytics.signInInitiated({ provider: "google", source })

			// OAuth here we come
			window.location.href = authUrl.toString()
		} catch (error) {
			console.error("Error during Google sign in:", error)
			Sentry.captureException(error)
			authError.value = "Google sign in failed for some reason. Please try again."
			isSubmitting.value = false
		}
	}

	const signInWithOutlook = async () => {
		isSubmitting.value = true
		authError.value = ""

		try {
			const clientId = import.meta.env.VITE_OUTLOOK_CLIENT_ID
			const redirectUri = `${window.location.origin}/auth/callback`
			const scope = "openid email profile"
			const state = Math.random().toString(36).substring(7)

			if (!clientId) {
				throw new Error("Outlook Client ID is not configured")
			}

			// keep the state in localStorage for verification and then remove it after in AuthCallbackPage.vue
			localStorage.setItem("oauth_state", state)
			// Store the provider type so we can detect it in the callback
			localStorage.setItem("oauth_provider", "outlook")
			localStorage.setItem("oauth_source", source)

			// Store the redirect parameter if exists, so we can use it after OAuth callback
			const redirectPath = getRedirectPath()
			if (redirectPath) {
				localStorage.setItem("oauth_redirect", redirectPath)
			}

			// Build the Microsoft OAuth URL
			const authUrl = new URL("https://login.microsoftonline.com/common/oauth2/v2.0/authorize")
			authUrl.searchParams.set("client_id", clientId)
			authUrl.searchParams.set("redirect_uri", redirectUri)
			authUrl.searchParams.set("response_type", "code")
			authUrl.searchParams.set("scope", scope)
			authUrl.searchParams.set("state", state)
			authUrl.searchParams.set("response_mode", "query")

			analytics.signInInitiated({ provider: "outlook", source })

			// OAuth here we come
			window.location.href = authUrl.toString()
		} catch (error) {
			console.error("Error during Outlook sign in:", error)
			Sentry.captureException(error)
			authError.value = "Outlook sign in failed for some reason. Please try again."
			isSubmitting.value = false
		}
	}

	return { isSubmitting, authError, signInWithGoogle, signInWithOutlook }
}
